import React from 'react';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import { useParams, Link } from 'react-router-dom';
import './BlogArticle.css';

const blogArticles = {
    'what-is-gfrp-sariya': {
        id: 'what-is-gfrp-sariya',
        title: 'What is GFRP Sariya and Why It Is the Future of Construction',
        content: [
            'Traditional steel reinforcement has been the backbone of construction for decades, but it comes with limitations—mainly corrosion, heavy weight, and long-term maintenance issues. This is where GFRP (Glass Fiber Reinforced Polymer) Sariya is transforming the construction industry.',
            'GFRP sariya is manufactured using high-strength glass fibers embedded in a polymer resin matrix. This combination creates reinforcement bars that are non-corrosive, lightweight, and exceptionally strong, making them ideal for modern infrastructure needs.',
            'Unlike steel, GFRP does not rust when exposed to moisture, chemicals, or coastal environments. This significantly increases the lifespan of structures such as bridges, water treatment plants, marine structures, and basements. Additionally, its lightweight nature reduces transportation and handling costs on-site.',
            'As construction moves toward durability, sustainability, and lower lifecycle costs, GFRP sariya is no longer an alternative—it is becoming the new standard.'
        ],
        image: '/blog1.webp',
        date: '2025-01-15',
        readTime: '5 min read'
    },
    'gfrp-vs-steel-reinforcement': {
        id: 'gfrp-vs-steel-reinforcement',
        title: 'GFRP vs Steel Reinforcement: A Structural Comparison',
        content: [
            'Choosing the right reinforcement material plays a crucial role in the longevity and safety of any structure. While steel has traditionally dominated the market, GFRP rebars are rapidly outperforming steel in critical parameters.',
            'Steel rebars are prone to corrosion, especially in humid, coastal, or chemically aggressive environments. Over time, rust expansion leads to cracking, spalling, and structural weakening. GFRP, on the other hand, is 100% corrosion-resistant, ensuring structural integrity over decades.',
            'In terms of weight, GFRP rebars are approximately 75% lighter than steel, making handling easier and reducing labor fatigue. They also offer high tensile strength, excellent thermal resistance, and electromagnetic neutrality—making them suitable for hospitals, power plants, and sensitive infrastructure.',
            'Although steel may have a lower upfront cost, GFRP significantly reduces maintenance, repair, and replacement costs, making it more economical over the structure\'s lifecycle.'
        ],
        image: '/blog2.webp',
        date: '2025-04-22',
        readTime: '6 min read'
    },
    'applications-of-gfrp-sariya': {
        id: 'applications-of-gfrp-sariya',
        title: 'Applications of GFRP Sariya in Modern Infrastructure',
        content: [
            'GFRP sariya is revolutionizing infrastructure projects where durability and performance are non-negotiable. Its corrosion resistance makes it the preferred choice for bridges, flyovers, coastal structures, and marine construction.',
            'In water-intensive environments such as sewage treatment plants, desalination facilities, and underground pipelines, GFRP rebars eliminate corrosion-related failures entirely. They are also widely used in basements, retaining walls, tunnels, and precast elements.',
            'Another major advantage is electrical and magnetic neutrality. This makes GFRP ideal for MRI rooms, power substations, and railway infrastructure, where steel interference can be problematic.',
            'As infrastructure demands grow more complex, GFRP sariya offers engineers the flexibility, strength, and longevity required for future-ready construction.'
        ],
        image: '/blog3.webp',
        date: '2025-08-28',
        readTime: '5 min read'
    },
    'why-builders-switching-gfrp': {
        id: 'why-builders-switching-gfrp',
        title: 'Why Builders and Engineers Are Switching to GFRP Reinforcement',
        content: [
            'The construction industry is evolving, and builders today prioritize materials that reduce risk, increase durability, and optimize costs. GFRP reinforcement checks all these boxes, which is why it is gaining rapid adoption across projects of all scales.',
            'One of the biggest reasons for the shift is long-term reliability. GFRP does not degrade due to corrosion, chemicals, or moisture, ensuring predictable structural performance. This drastically reduces inspection and maintenance cycles.',
            'From a project management perspective, GFRP rebars are easier to transport, cut, and install. Their lightweight nature accelerates construction timelines while maintaining structural strength.',
            'With increasing awareness, regulatory acceptance, and proven field performance, GFRP reinforcement is no longer experimental—it is a smart engineering choice for forward-thinking builders and developers.'
        ],
        image: '/blog4.webp',
        date: '2025-11-05',
        readTime: '6 min read'
    }
};

function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
}

function BlogArticle() {
    const { articleId } = useParams();
    const article = blogArticles[articleId];

    // Get related articles (all articles except current one)
    const relatedArticles = Object.values(blogArticles)
        .filter(a => a.id !== articleId)
        .slice(0, 3);

    if (!article) {
        return (
            <div className="blog-article">
                <Navbar />
                <div className="blog-article-not-found">
                    <div className="blog-article-not-found-icon">📄</div>
                    <h1>Article Not Found</h1>
                    <p>The article you're looking for doesn't exist or may have been moved.</p>
                    <Link to="/blog" className="blog-article-back-link-bottom">
                        ← Browse All Articles
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="blog-article">
            <Navbar />

            {/* Article Header */}
            <div className="blog-article-header-section">
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: 'url(/construction.webp)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.06,
                        zIndex: 1
                    }}
                />
                <div className="blog-article-header-container">
                    <Link to="/blog" className="blog-article-back-link">
                        ← Back to Blog
                    </Link>
                    <div className="blog-article-meta-header">
                        <span className="blog-article-date-header">{formatDate(article.date)}</span>
                        <span className="blog-article-read-time-header">{article.readTime}</span>
                    </div>
                    <h1 className="blog-article-title-header">{article.title}</h1>
                </div>
            </div>

            {/* Featured Image */}
            <div className="blog-article-image-section">
                <img
                    src={article.image}
                    alt={article.title}
                    className="blog-article-featured-image"
                />
            </div>

            {/* Article Content */}
            <div className="blog-article-content-section">
                <article className="blog-article-content-wrapper">
                    <p className="blog-article-intro">
                        {article.content[0]}
                    </p>
                    {article.content.slice(1).map((paragraph, index) => (
                        <p key={index} className="blog-article-paragraph">
                            {paragraph}
                        </p>
                    ))}
                </article>
            </div>

            {/* Article Footer */}
            <div className="blog-article-footer-section">
                <div className="blog-article-divider"></div>
                <div className="blog-article-footer-content">
                    <Link to="/blog" className="blog-article-back-link-bottom">
                        ← All Articles
                    </Link>
                    {/* <div className="blog-article-share">
                        <span className="blog-article-share-text">Share:</span>
                        <div className="blog-article-share-buttons">
                            <button className="blog-article-share-button" title="Share on LinkedIn">
                                💼
                            </button>
                            <button className="blog-article-share-button" title="Share on Twitter">
                                🐦
                            </button>
                            <button className="blog-article-share-button" title="Share via Email">
                                ✉️
                            </button>
                        </div>
                    </div> */}
                </div>
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
                <div className="blog-article-related-section">
                    <div className="blog-article-related-container">
                        <div className="blog-article-related-header">
                            <h2 className="blog-article-related-title">Continue Reading</h2>
                            <p className="blog-article-related-subtitle">
                                Explore more insights on GFRP and sustainable construction
                            </p>
                        </div>
                        <div className="blog-article-related-grid">
                            {relatedArticles.map((relatedArticle) => (
                                <Link
                                    key={relatedArticle.id}
                                    to={`/blog/${relatedArticle.id}`}
                                    className="blog-article-related-card"
                                >
                                    <div className="blog-article-related-image-wrapper">
                                        <img
                                            src={relatedArticle.image}
                                            alt={relatedArticle.title}
                                            className="blog-article-related-image"
                                        />
                                    </div>
                                    <div className="blog-article-related-content">
                                        <h3 className="blog-article-related-card-title">
                                            {relatedArticle.title}
                                        </h3>
                                        <div className="blog-article-related-card-meta">
                                            <span>{formatDate(relatedArticle.date)}</span>
                                            <span>•</span>
                                            <span>{relatedArticle.readTime}</span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}

export default BlogArticle;